import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Get the browser's timezone
 * @returns {string} The browser's timezone (e.g., 'America/New_York')
 */
export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York'; // Fallback to Eastern
  }
};

/**
 * Get the effective timezone based on user settings
 * @param {boolean} useBrowserTimeZone - Whether to use browser timezone
 * @param {string|null} userTimeZone - The user's selected timezone
 * @returns {string} The effective timezone to use
 */
export const getEffectiveTimezone = (useBrowserTimeZone, userTimeZone) => {
  if (useBrowserTimeZone || !userTimeZone) {
    return getBrowserTimezone();
  }
  return userTimeZone;
};

/**
 * Format a date in the specified timezone
 * @param {string|Date|dayjs.Dayjs} date - The date to format
 * @param {string} format - The format string (dayjs format)
 * @param {string} tz - The timezone to use
 * @returns {string} The formatted date string
 */
export const formatInTimezone = (date, format, tz) => {
  if (!date) return '';

  return dayjs(date).tz(tz).format(format);
};

/**
 * Create a dayjs instance in the specified timezone
 * @param {string|Date|dayjs.Dayjs} date - The date
 * @param {string} tz - The timezone
 * @returns {dayjs.Dayjs} A dayjs instance in the specified timezone
 */
export const dayjsInTimezone = (date, tz) => {
  if (!date) return dayjs().tz(tz);
  return dayjs(date).tz(tz);
};

/**
 * Convert a time string (HH:mm) from a specific timezone to UTC
 * @param {string} timeStr - Time string in "HH:mm" format
 * @param {string} fromTz - The source timezone
 * @returns {string} Time string in "HH:mm" format in UTC
 */
export const convertTimeToUTC = (timeStr, fromTz) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  // Create a date in the source timezone, then convert to UTC
  const localTime = dayjs().tz(fromTz).hour(hours).minute(minutes).second(0).millisecond(0);
  return localTime.utc().format('HH:mm');
};

/**
 * Convert a time string (HH:mm) from UTC to a specific timezone
 * @param {string} timeStr - Time string in "HH:mm" format (UTC)
 * @param {string} toTz - The target timezone
 * @returns {string} Time string in "HH:mm" format in the target timezone
 */
export const convertTimeFromUTC = (timeStr, toTz) => {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  // Create a UTC date, then convert to target timezone
  const utcTime = dayjs.utc().hour(hours).minute(minutes).second(0).millisecond(0);
  return utcTime.tz(toTz).format('HH:mm');
};

/**
 * Calculate the next scheduled time based on an array of scheduled times (HH:mm format in UTC)
 * @param {string[]} scheduledTimes - Array of time strings in "HH:mm" format (UTC)
 * @param {string} tz - The user's timezone for display
 * @returns {dayjs.Dayjs|null} The next scheduled time as a dayjs instance in user's timezone, or null if no valid times
 */
export const getNextScheduledTime = (scheduledTimes, tz) => {
  if (!scheduledTimes || !Array.isArray(scheduledTimes) || scheduledTimes.length === 0) {
    return null;
  }

  // Filter out null/empty values
  const validTimes = scheduledTimes.filter((time) => time && typeof time === 'string');
  if (validTimes.length === 0) {
    return null;
  }

  // Get current time in UTC for comparison
  const nowUtc = dayjs.utc();

  let nextTime = null;
  let minDiff = Infinity;

  for (const timeStr of validTimes) {
    // Parse the UTC time string (HH:mm)
    const [hours, minutes] = timeStr.split(':').map(Number);

    // Create a UTC date for today at this time
    let scheduledDateUtc = nowUtc.hour(hours).minute(minutes).second(0).millisecond(0);

    // If this time has already passed today (in UTC), use tomorrow
    if (scheduledDateUtc.isBefore(nowUtc)) {
      scheduledDateUtc = scheduledDateUtc.add(1, 'day');
    }

    // Calculate the difference from now
    const diff = scheduledDateUtc.diff(nowUtc, 'millisecond');

    if (diff > 0 && diff < minDiff) {
      minDiff = diff;
      // Convert to user's timezone for display
      nextTime = scheduledDateUtc.tz(tz);
    }
  }

  return nextTime;
};

export { dayjs };
